<template>
  <div class="profile-info-page">
    <header class="pinfo-top">
      <button type="button" class="pinfo-back" aria-label="返回" @click="goBack">
        <i class="el-icon-arrow-left"></i>
      </button>
      <h1 class="pinfo-title">个人资料</h1>
      <span class="pinfo-top-spacer" aria-hidden="true" />
    </header>

    <el-drawer
      :visible.sync="avatarPickerVisible"
      direction="btt"
      :with-header="false"
      :append-to-body="true"
      :wrapper-closable="true"
      :show-close="false"
      size="200px"
      custom-class="profile-avatar-sheet"
    >
      <div class="profile-avatar-sheet__inner">
        <div class="profile-avatar-sheet__handle" aria-hidden="true" />
        <button type="button" class="profile-avatar-sheet__reset" @click="selectAvatarPreset('')">
          默认图标
        </button>
        <div class="profile-avatar-options">
          <button
            type="button"
            class="profile-avatar-option"
            :class="{ 'is-current': avatarPreset === 'man' }"
            aria-label="男"
            @click="selectAvatarPreset('man')"
          >
            <span class="profile-avatar-option__img-wrap">
              <img :src="defaultManImg" alt="" class="profile-avatar-option__img" />
            </span>
          </button>
          <button
            type="button"
            class="profile-avatar-option"
            :class="{ 'is-current': avatarPreset === 'woman' }"
            aria-label="女"
            @click="selectAvatarPreset('woman')"
          >
            <span class="profile-avatar-option__img-wrap">
              <img :src="defaultWomanImg" alt="" class="profile-avatar-option__img" />
            </span>
          </button>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      :visible.sync="fieldDialogVisible"
      width="88%"
      top="26vh"
      append-to-body
      custom-class="pinfo-field-dialog"
      :show-close="true"
      :close-on-click-modal="false"
      @closed="onFieldDialogClosed"
    >
      <div slot="title" class="pinfo-field-dialog__head">
        <h3 class="pinfo-field-dialog__title">{{ fieldDialogTitle }}</h3>
      </div>
      <div class="pinfo-field-dialog__panel">
        <div
          v-if="fieldDialogType === 'phone'"
          class="pinfo-phone-field"
          :class="{ 'is-focused': phoneFieldFocused }"
        >
          <i class="el-icon-mobile-phone pinfo-phone-field__icon"></i>
          <el-select
            v-model="fieldCountryCode"
            filterable
            class="pinfo-country-select"
            popper-class="pinfo-country-dropdown"
            @visible-change="onPhoneCountryVisible"
          >
            <el-option
              v-for="item in countryOptions"
              :key="item.code + '-' + item.label"
              :label="item.dialLabel"
              :value="item.code"
            >
              <span class="pinfo-country-option__label">{{ item.label }}</span>
              <span class="pinfo-country-option__dial">{{ item.dialLabel }}</span>
            </el-option>
          </el-select>
          <span class="pinfo-phone-field__divider" aria-hidden="true"></span>
          <el-input
            v-model="fieldInputValue"
            type="tel"
            maxlength="20"
            placeholder="请输入手机号"
            clearable
            class="pinfo-phone-input"
            @input="onPhoneFieldInput"
            @focus="phoneFieldFocused = true"
            @blur="phoneFieldFocused = false"
          />
        </div>
        <el-input
          v-else
          v-model="fieldInputValue"
          type="email"
          placeholder="请输入邮箱"
          clearable
          class="pinfo-field-dialog__input"
        >
          <template slot="prefix">
            <i class="el-icon-message"></i>
          </template>
        </el-input>
      </div>
      <div slot="footer" class="pinfo-field-dialog__actions">
        <button
          type="button"
          class="pinfo-field-dialog__btn pinfo-field-dialog__btn--ghost"
          @click="fieldDialogVisible = false"
        >
          取消
        </button>
        <button
          type="button"
          class="pinfo-field-dialog__btn pinfo-field-dialog__btn--primary"
          @click="confirmFieldDialog"
        >
          保存
        </button>
      </div>
    </el-dialog>

    <div v-loading="queryInfoLoading" class="pinfo-body" element-loading-text="加载中…">
      <section class="pinfo-card pinfo-card--avatar">
        <button type="button" class="pinfo-avatar-btn" aria-label="更换头像" @click="avatarPickerVisible = true">
          <span class="pinfo-avatar">
            <img v-if="avatarImageUrl" :src="avatarImageUrl" alt="" class="pinfo-avatar__img" />
            <i v-else class="el-icon-user-solid" aria-hidden="true"></i>
          </span>
        </button>
      </section>

      <section class="pinfo-card pinfo-fields">
        <div class="pinfo-row pinfo-row--static">
          <span class="pinfo-label">账号</span>
          <span class="pinfo-value">{{ displayUserName || '—' }}</span>
        </div>
        <div class="pinfo-row pinfo-row--static">
          <span class="pinfo-label">ID</span>
          <span class="pinfo-value">{{ displayUserId || '—' }}</span>
        </div>
        <div class="pinfo-row pinfo-row--control">
          <span class="pinfo-label">性别</span>
          <el-select
            v-model="genderModel"
            size="small"
            placeholder="请选择"
            class="pinfo-select"
            popper-class="pinfo-select-dropdown"
            clearable
            @change="onGenderChange"
          >
            <el-option label="未知" value="0" />
            <el-option label="男" value="1" />
            <el-option label="女" value="2" />
          </el-select>
        </div>
        <button type="button" class="pinfo-row pinfo-row--link" @click="goFieldEdit('phone')">
          <span class="pinfo-label">手机号</span>
          <span class="pinfo-value pinfo-value--muted">{{ phoneDisplay }}</span>
          <i class="el-icon-arrow-right pinfo-chevron"></i>
        </button>
        <button type="button" class="pinfo-row pinfo-row--link" @click="goFieldEdit('email')">
          <span class="pinfo-label">邮箱地址</span>
          <span class="pinfo-value pinfo-value--muted">{{ emailDisplay }}</span>
          <i class="el-icon-arrow-right pinfo-chevron"></i>
        </button>
      </section>

      <div class="pinfo-actions">
        <el-button
          type="primary"
          class="pinfo-update-btn"
          :loading="updateSubmitting"
          @click="handleUpdateProfile"
        >
          更新资料
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import defaultManImg from '@/assets/default-man.jpg?url'
import defaultWomanImg from '@/assets/default-woman.jpg?url'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { resolveResourceUrl } from '@/utils/resourceUrl'
import {
  getStoredUserProfile,
  getProfileAvatarPreset,
  saveProfileAvatarPreset,
  saveStoredGender,
  getProfilePhone,
  getProfileEmail,
  saveProfilePhone,
  saveProfileEmail,
  applyServerUserProfile
} from '@/utils/userSession'
import {
  PHONE_COUNTRY_OPTIONS,
  buildPhoneForSubmit,
  validatePhoneDigits,
  parseStoredPhone
} from '@/utils/phoneCountry'

const USER_QUERY_INFO_PATH = '/api/app/core/user/queryInfo'
const USER_UPDATE_INFO_PATH = '/api/app/core/user/updateInfo'
const PRESET_AVATAR_PATH_MAN = '/preset-avatar/man'
const PRESET_AVATAR_PATH_WOMAN = '/preset-avatar/woman'

export default {
  name: 'ProfileInfoPage',
  data() {
    return {
      defaultManImg,
      defaultWomanImg,
      profile: getStoredUserProfile(),
      avatarPreset: getProfileAvatarPreset(),
      avatarPickerVisible: false,
      genderModel: '',
      updateSubmitting: false,
      queryInfoLoading: false,
      fieldDialogVisible: false,
      fieldDialogType: 'phone',
      fieldInputValue: '',
      fieldCountryCode: '86',
      phoneFieldFocused: false,
      countryOptions: PHONE_COUNTRY_OPTIONS,
      /** 与 localStorage 同步，供列表回显（computed 不能直接依赖 getProfilePhone 等非响应式读） */
      profilePhoneSnap: getProfilePhone(),
      profileEmailSnap: getProfileEmail()
    }
  },
  computed: {
    avatarImageUrl() {
      const serverAvatar = this.profile && this.profile.avatar ? String(this.profile.avatar).trim() : ''
      if (serverAvatar === PRESET_AVATAR_PATH_MAN) return this.defaultManImg
      if (serverAvatar === PRESET_AVATAR_PATH_WOMAN) return this.defaultWomanImg
      if (serverAvatar) return resolveResourceUrl(serverAvatar)
      if (this.avatarPreset === 'man') return this.defaultManImg
      if (this.avatarPreset === 'woman') return this.defaultWomanImg
      return ''
    },
    displayUserId() {
      return (this.profile && this.profile.userId) || ''
    },
    displayUserName() {
      const n = this.profile && this.profile.userName
      return (n && String(n).trim()) || ''
    },
    phoneDisplay() {
      const p = this.profilePhoneSnap
      return (p != null && String(p).trim() !== '') ? String(p).trim() : '去填写'
    },
    emailDisplay() {
      const e = this.profileEmailSnap
      return (e != null && String(e).trim() !== '') ? String(e).trim() : '去填写'
    },
    fieldDialogTitle() {
      return this.fieldDialogType === 'email' ? '修改邮箱地址' : '修改手机号'
    }
  },
  mounted() {
    this.reloadLocal()
    this.loadProfileFromServer()
  },
  activated() {
    this.reloadLocal()
    this.loadProfileFromServer()
  },
  methods: {
    resolvePresetAvatarPath(preset) {
      const p = preset === 'man' || preset === 'woman' ? preset : ''
      if (!p) return ''
      return p === 'man' ? PRESET_AVATAR_PATH_MAN : PRESET_AVATAR_PATH_WOMAN
    },
    normalizeAvatarPathForPayload(raw) {
      const s = raw != null ? String(raw).trim() : ''
      if (!s) return ''
      if (/^(data:|blob:)/i.test(s)) return ''
      if (/^https?:\/\//i.test(s)) {
        try {
          const u = new URL(s)
          return `${u.pathname || ''}${u.search || ''}${u.hash || ''}` || ''
        } catch (e) {
          void e
          return ''
        }
      }
      return s
    },
    unwrapApiData(resData, failMsg) {
      if (!resData || typeof resData !== 'object') return {}
      if (!Object.prototype.hasOwnProperty.call(resData, 'code')) {
        return resData
      }
      const code = typeof resData.code === 'string' ? Number(String(resData.code).trim()) : Number(resData.code)
      if (Number.isNaN(code) || code !== 200) {
        const msg =
          resData.msg != null && String(resData.msg).trim() !== '' ? String(resData.msg) : failMsg || '请求失败'
        throw new Error(msg)
      }
      return resData.data != null && typeof resData.data === 'object' ? resData.data : {}
    },
    async loadProfileFromServer() {
      const profile = getStoredUserProfile()
      const userId = profile.userId != null ? String(profile.userId).trim() : ''
      const username = profile.userName != null ? String(profile.userName).trim() : ''
      if (!userId && !username) {
        return
      }
      this.queryInfoLoading = true
      try {
        const { data } = await axios.post(USER_QUERY_INFO_PATH, { userId, username })
        const inner = this.unwrapApiData(data, '加载资料失败')
        const user =
          inner && typeof inner.user === 'object' && !Array.isArray(inner.user) ? inner.user : inner
        if (user && typeof user === 'object' && Object.keys(user).length) {
          applyServerUserProfile(user)
        }
        this.reloadLocal()
      } catch (e) {
        this.$message.error(getApiErrorMessage(e, '加载资料失败，将显示本地缓存'))
      } finally {
        this.queryInfoLoading = false
      }
    },
    reloadLocal() {
      this.profile = { ...getStoredUserProfile() }
      this.avatarPreset = getProfileAvatarPreset()
      this.profilePhoneSnap = getProfilePhone()
      this.profileEmailSnap = getProfileEmail()
      const g = (this.profile.gender && String(this.profile.gender).trim()) || ''
      this.genderModel = ['0', '1', '2'].includes(g) ? g : ''
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/profile')
      }
    },
    async selectAvatarPreset(v) {
      const preset = v === 'man' || v === 'woman' ? v : ''
      const avatarPath = this.resolvePresetAvatarPath(preset)
      saveProfileAvatarPreset(preset)
      applyServerUserProfile({
        avatarPreset: preset || '',
        avatar: avatarPath
      })
      this.profile = { ...getStoredUserProfile() }
      this.avatarPreset = getProfileAvatarPreset()
      this.avatarPickerVisible = false
      await this.syncAvatarPathToServer(avatarPath)
    },
    async syncAvatarPathToServer(avatarPath) {
      const profile = getStoredUserProfile()
      const userId = profile.userId != null ? String(profile.userId).trim() : ''
      const username = profile.userName != null ? String(profile.userName).trim() : ''
      if (!username && !userId) return
      const gStored = profile.gender != null ? String(profile.gender).trim() : ''
      const gStr = gStored === '0' || gStored === '1' || gStored === '2' ? gStored : '0'
      const payload = {
        userId,
        username,
        phone: getProfilePhone(),
        gender: Number(gStr),
        email: getProfileEmail() || '',
        avatar: avatarPath || ''
      }
      try {
        console.info('[ProfileInfo] sync avatar payload:', payload)
        const { data } = await axios.post(USER_UPDATE_INFO_PATH, payload)
        const inner = this.unwrapApiData(data, '头像更新失败')
        const serverUser =
          inner && typeof inner.user === 'object' && !Array.isArray(inner.user) ? inner.user : inner
        if (serverUser && typeof serverUser === 'object' && Object.keys(serverUser).length) {
          applyServerUserProfile(serverUser)
          this.profile = { ...getStoredUserProfile() }
        }
      } catch (e) {
        this.$message.error(getApiErrorMessage(e, '头像保存失败，请重试'))
      }
    },
    onGenderChange(val) {
      const v = val === '0' || val === '1' || val === '2' ? val : '0'
      saveStoredGender(v)
      this.genderModel = v
      this.profile = { ...getStoredUserProfile() }
    },
    goFieldEdit(type) {
      const t = type === 'email' ? 'email' : 'phone'
      this.fieldDialogType = t
      if (t === 'email') {
        this.fieldInputValue = getProfileEmail()
        this.fieldCountryCode = '86'
      } else {
        const parsed = parseStoredPhone(getProfilePhone())
        this.fieldCountryCode = parsed.countryCode
        this.fieldInputValue = parsed.phone
      }
      this.phoneFieldFocused = false
      this.fieldDialogVisible = true
    },
    onFieldDialogClosed() {
      this.fieldInputValue = ''
      this.fieldCountryCode = '86'
      this.phoneFieldFocused = false
    },
    onPhoneFieldInput(val) {
      const digits = String(val != null ? val : '').replace(/\D/g, '')
      if (digits !== this.fieldInputValue) {
        this.fieldInputValue = digits
      }
    },
    onPhoneCountryVisible(visible) {
      this.phoneFieldFocused = !!visible
    },
    confirmFieldDialog() {
      if (this.fieldDialogType === 'phone') {
        const err = validatePhoneDigits(this.fieldCountryCode, this.fieldInputValue)
        if (err) {
          this.$message.warning(err === '请输入手机号' ? '手机号不能为空' : err)
          return
        }
        const phone = buildPhoneForSubmit(this.fieldCountryCode, this.fieldInputValue)
        if (!phone) {
          this.$message.warning('请输入有效手机号')
          return
        }
        saveProfilePhone(phone)
      } else {
        const v = String(this.fieldInputValue != null ? this.fieldInputValue : '').trim()
        if (!v) {
          this.$message.warning('邮箱不能为空')
          return
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
          this.$message.warning('请输入有效邮箱地址')
          return
        }
        saveProfileEmail(v)
      }
      this.fieldDialogVisible = false
      this.reloadLocal()
    },
    async handleUpdateProfile() {
      const profile = getStoredUserProfile()
      const gSel = this.genderModel
      const gStored = profile.gender != null ? String(profile.gender).trim() : ''
      const gStr =
        gSel === '0' || gSel === '1' || gSel === '2'
          ? gSel
          : gStored === '0' || gStored === '1' || gStored === '2'
            ? gStored
            : '0'
      const genderNum = Number(gStr)
      const avatarPath = this.normalizeAvatarPathForPayload(profile.avatar)
      const payload = {
        userId: profile.userId != null ? String(profile.userId).trim() : '',
        username: (profile.userName && String(profile.userName).trim()) || '',
        phone: getProfilePhone(),
        gender: genderNum,
        email: getProfileEmail() || '',
        avatar: avatarPath
      }
      this.updateSubmitting = true
      try {
        const { data } = await axios.post(USER_UPDATE_INFO_PATH, payload)
        const inner = this.unwrapApiData(data, '更新失败')
        const serverUser =
          inner && typeof inner.user === 'object' && !Array.isArray(inner.user) ? inner.user : inner
        const merged = {
          userId: profile.userId,
          username: payload.username,
          gender: genderNum,
          phone: payload.phone,
          email: payload.email,
          avatar: avatarPath,
          avatarPreset: getProfileAvatarPreset() || undefined
        }
        if (serverUser && typeof serverUser === 'object' && Object.keys(serverUser).length) {
          Object.assign(merged, serverUser)
        }
        applyServerUserProfile(merged)
        this.$message.success('已更新')
        this.$router.replace('/profile')
      } catch (e) {
        this.$message.error(getApiErrorMessage(e, '更新失败，请重试'))
      } finally {
        this.updateSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.profile-info-page {
  --pinfo-primary: #4253ff;
  --pinfo-primary-deep: #3546e8;
  --pinfo-ink: #0a1c44;
  --pinfo-muted: #6b7a94;
  --pinfo-line: #eceef3;
  --pinfo-soft: #f4f6fa;
  --pinfo-page: #f5f7fb;
  min-height: 100%;
  background: var(--pinfo-page);
  box-sizing: border-box;
}

.pinfo-top {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  padding: 0 10px 0 6px;
  border-bottom: 1px solid var(--pinfo-line);
  background: rgba(255, 255, 255, 0.92);
}

.pinfo-back {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--pinfo-muted);
  font-size: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.pinfo-back:active {
  background: var(--pinfo-soft);
  color: var(--pinfo-primary);
}

.pinfo-title {
  flex: 1;
  margin: 0;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  color: var(--pinfo-ink);
}

.pinfo-top-spacer {
  width: 40px;
  flex-shrink: 0;
}

.pinfo-body {
  padding: 16px 14px 28px;
  max-width: 560px;
  margin: 0 auto;
  box-sizing: border-box;
}

.pinfo-card {
  background: #fff;
  border: 1px solid var(--pinfo-line);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(15, 28, 68, 0.04);
  margin-bottom: 12px;
}

.pinfo-card--avatar {
  display: flex;
  justify-content: center;
  padding: 20px 16px;
}

.pinfo-avatar-btn {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  -webkit-tap-highlight-color: transparent;
}

.pinfo-avatar-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.18);
}

.pinfo-avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: #eef1ff;
  border: 1px solid rgba(66, 83, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--pinfo-primary);
  font-size: 40px;
}

.pinfo-avatar__img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.pinfo-fields {
  padding: 4px 0;
}

.pinfo-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 6px 16px;
  box-sizing: border-box;
}

.pinfo-row + .pinfo-row {
  border-top: 1px solid var(--pinfo-line);
}

.pinfo-row--static .pinfo-value {
  text-align: right;
}

.pinfo-row--control .pinfo-select {
  flex: 0 0 auto;
  width: 88px;
  margin-left: auto;
  -webkit-tap-highlight-color: transparent;
}

.pinfo-select /deep/ .el-input__inner {
  text-align: center;
  padding-left: 22px;
  padding-right: 22px;
  border-radius: 10px;
  border-color: var(--pinfo-line);
  background: var(--pinfo-soft);
  color: var(--pinfo-ink);
  -webkit-tap-highlight-color: transparent;
}

.pinfo-select /deep/ .el-input__suffix {
  right: 4px;
}

.pinfo-select /deep/ .el-input__inner:focus {
  outline: none;
}

.pinfo-select /deep/ .el-input.is-focus .el-input__inner,
.pinfo-select /deep/ .el-input__inner:focus {
  border-color: var(--pinfo-primary);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.pinfo-select /deep/ .el-input__inner:active {
  background-color: #fff;
}

.pinfo-row--link {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}

.pinfo-row--link:active {
  background: var(--pinfo-soft);
}

.pinfo-label {
  flex-shrink: 0;
  width: 76px;
  font-size: 14px;
  color: var(--pinfo-muted);
  font-weight: 500;
}

.pinfo-value {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: var(--pinfo-ink);
  word-break: break-all;
  text-align: right;
}

.pinfo-value--muted {
  color: #98a2b3;
}

.pinfo-chevron {
  flex-shrink: 0;
  font-size: 14px;
  color: #c5cad6;
}

.pinfo-actions {
  margin-top: 16px;
  padding: 0 2px;
}

.pinfo-update-btn {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  border: none !important;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: linear-gradient(135deg, var(--pinfo-primary) 0%, var(--pinfo-primary-deep) 100%) !important;
  color: #fff !important;
  box-shadow: 0 8px 20px rgba(66, 83, 255, 0.28);
}

.pinfo-update-btn:hover,
.pinfo-update-btn:focus {
  background: linear-gradient(135deg, #5364ff 0%, var(--pinfo-primary) 100%) !important;
  color: #fff !important;
}
</style>

<style>
.profile-avatar-sheet.el-drawer {
  border-radius: 18px 18px 0 0;
  box-shadow: 0 -8px 28px rgba(15, 28, 68, 0.1);
}

.profile-avatar-sheet .el-drawer__body {
  padding: 0;
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  height: 100%;
  box-sizing: border-box;
}

.profile-avatar-sheet__inner {
  padding: 10px 18px 6px;
  box-sizing: border-box;
}

.profile-avatar-sheet__handle {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #eceef3;
  margin: 0 auto 10px;
}

.profile-avatar-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  max-width: 200px;
  margin: 0 auto;
}

.profile-avatar-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 2px solid #eceef3;
  border-radius: 12px;
  background: #f8f9fc;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.profile-avatar-option:hover {
  border-color: rgba(66, 83, 255, 0.35);
  background: #eef1ff;
}

.profile-avatar-option.is-current {
  border-color: #4253ff;
  box-shadow: 0 0 0 1px rgba(66, 83, 255, 0.12);
  background: #eef1ff;
}

.profile-avatar-option__img-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(10, 28, 68, 0.08);
  flex-shrink: 0;
}

.profile-avatar-option__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-avatar-sheet__reset {
  display: block;
  width: 100%;
  margin: 0 auto 10px;
  padding: 6px 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7a94;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.profile-avatar-sheet__reset:active {
  background: #f4f6fa;
  color: #4253ff;
}

.pinfo-select-dropdown.el-select-dropdown {
  min-width: 88px !important;
  width: 88px !important;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid #eceef3;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 28, 68, 0.1);
  overflow: hidden;
}

.pinfo-select-dropdown .el-select-dropdown__item {
  padding: 0 12px;
  text-align: center;
}

.pinfo-select-dropdown .el-select-dropdown__item.hover,
.pinfo-select-dropdown .el-select-dropdown__item:hover {
  background-color: #f4f6fa !important;
}

.pinfo-select-dropdown .el-select-dropdown__item.selected {
  color: #4253ff;
  font-weight: 600;
  background-color: #eef1ff !important;
}

.pinfo-select-dropdown .el-select-dropdown__item.selected.hover,
.pinfo-select-dropdown .el-select-dropdown__item.selected:hover {
  background-color: #e8ebff !important;
}

.pinfo-field-dialog.el-dialog {
  max-width: 400px;
  margin-top: 26vh !important;
  border-radius: 20px !important;
  overflow: hidden;
  border: 1px solid #eceef3 !important;
  box-shadow:
    0 8px 28px rgba(15, 28, 68, 0.08),
    0 2px 8px rgba(15, 28, 68, 0.04) !important;
  background: #ffffff;
}

.pinfo-field-dialog .el-dialog__header {
  padding: 20px 20px 0 !important;
  border-bottom: none !important;
}

.pinfo-field-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
}

.pinfo-field-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #98a2b3 !important;
  font-size: 16px;
}

.pinfo-field-dialog .el-dialog__headerbtn:hover .el-dialog__close,
.pinfo-field-dialog .el-dialog__headerbtn:focus .el-dialog__close {
  color: #4253ff !important;
}

.pinfo-field-dialog .el-dialog__body {
  padding: 14px 20px 0 !important;
  color: #0a1c44 !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
}

.pinfo-field-dialog .el-dialog__footer {
  padding: 18px 20px 20px !important;
  border-top: none;
}

.pinfo-field-dialog__head {
  padding-right: 28px;
}

.pinfo-field-dialog__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0a1c44;
  letter-spacing: 0.02em;
  line-height: 1.35;
}

.pinfo-field-dialog__hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #6b7a94;
  line-height: 1.5;
}

.pinfo-field-dialog__panel {
  box-sizing: border-box;
}

.pinfo-phone-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  padding: 0 8px 0 12px;
  border-radius: 12px;
  background: #f4f6fa;
  border: 1px solid #eceef3;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.pinfo-phone-field:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.pinfo-phone-field.is-focused {
  background: #fff;
  border-color: #4253ff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.pinfo-phone-field__icon {
  flex: 0 0 auto;
  width: 22px;
  font-size: 18px;
  color: #8b95a8;
  text-align: center;
  line-height: 1;
}

.pinfo-country-select {
  flex: 0 0 auto;
  width: 54px;
}

.pinfo-country-select .el-input__inner {
  height: 44px;
  line-height: 44px;
  padding: 0 14px 0 0;
  border: none;
  background: transparent;
  box-shadow: none;
  font-size: 13px;
  font-weight: 500;
  color: #0a1c44;
  text-align: center;
}

.pinfo-country-select .el-input__inner:hover,
.pinfo-country-select .el-input.is-focus .el-input__inner,
.pinfo-country-select .el-input__inner:focus {
  background: transparent;
  border: none;
  box-shadow: none;
}

.pinfo-country-select .el-input__suffix {
  right: 0;
}

.pinfo-country-select .el-input__suffix .el-input__icon {
  line-height: 44px;
  width: 16px;
  color: #8b95a8;
}

.pinfo-phone-field__divider {
  flex: 0 0 auto;
  width: 1px;
  height: 18px;
  margin: 0 6px 0 2px;
  background: rgba(10, 28, 68, 0.12);
}

.pinfo-phone-input {
  flex: 1;
  min-width: 0;
}

.pinfo-phone-input .el-input__inner {
  height: 44px;
  line-height: 44px;
  padding-left: 8px;
  padding-right: 30px;
  border: none;
  background: transparent;
  box-shadow: none;
  font-size: 15px;
  color: #0a1c44;
}

.pinfo-phone-input .el-input__inner:hover,
.pinfo-phone-input .el-input__inner:focus {
  background: transparent;
  border: none;
  box-shadow: none;
}

.pinfo-phone-input .el-input__inner::placeholder {
  color: #9aa5b8;
}

.pinfo-phone-input .el-input__suffix .el-input__icon {
  line-height: 44px;
}

.pinfo-country-dropdown.el-select-dropdown {
  border-radius: 12px;
  border: 1px solid rgba(10, 28, 68, 0.08);
  box-shadow: 0 10px 28px rgba(10, 28, 68, 0.12);
  overflow: hidden;
}

.pinfo-country-dropdown .el-select-dropdown__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 14px;
  height: 40px;
  line-height: 40px;
  font-size: 14px;
}

.pinfo-country-dropdown .el-select-dropdown__item.hover,
.pinfo-country-dropdown .el-select-dropdown__item:hover {
  background-color: #f4f6fa;
}

.pinfo-country-dropdown .el-select-dropdown__item.selected {
  color: #4253ff;
  font-weight: 600;
  background-color: #eef1ff;
}

.pinfo-country-option__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pinfo-country-option__dial {
  flex-shrink: 0;
  color: #6b7a94;
  font-weight: 500;
}

.pinfo-country-dropdown .el-select-dropdown__item.selected .pinfo-country-option__dial {
  color: #4253ff;
}

.pinfo-field-dialog__input {
  width: 100%;
}

.pinfo-field-dialog__input.el-input--prefix .el-input__inner,
.pinfo-field-dialog__input .el-input__inner {
  height: 48px;
  line-height: 48px;
  padding-left: 48px !important;
  border-radius: 12px;
  border: 1px solid #eceef3;
  background: #f4f6fa;
  color: #0a1c44;
  font-size: 15px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.pinfo-field-dialog__input .el-input__inner::placeholder {
  color: #9aa5b8;
}

.pinfo-field-dialog__input .el-input__prefix {
  left: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8b95a8;
  pointer-events: none;
}

.pinfo-field-dialog__input .el-input__prefix i {
  font-size: 18px;
  line-height: 1;
  margin: 0;
}

.pinfo-field-dialog__input .el-input__suffix {
  display: flex;
  align-items: center;
  right: 8px;
}

.pinfo-field-dialog__input .el-input__suffix .el-input__icon {
  line-height: 48px;
}

.pinfo-field-dialog__input .el-input__inner:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.pinfo-field-dialog__input .el-input__inner:focus {
  border-color: #4253ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.pinfo-field-dialog__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
}

.pinfo-field-dialog__btn {
  height: 44px;
  border-radius: 12px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.pinfo-field-dialog__btn--ghost {
  background: #f4f6fa;
  color: #6b7a94;
}

.pinfo-field-dialog__btn--ghost:active {
  background: #e8ebf2;
  color: #0a1c44;
}

.pinfo-field-dialog__btn--primary {
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #fff;
  box-shadow: 0 6px 16px rgba(66, 83, 255, 0.28);
}

.pinfo-field-dialog__btn--primary:active {
  opacity: 0.92;
}
</style>
